import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api, formatApiError } from "@/lib/api";
import { readLocalSettings, writeLocalSettings, SETTINGS_DEFAULTS } from "@/lib/settings";
import { Switch } from "@/components/ui/switch";
import { Check, Moon, Sun, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { refreshCrowns } from "@/lib/crowns";
import { PLAYER_COLOURS, resolveColours } from "@/lib/playerColours";

// Keyboard highlight styles. Colours match updateHandKeys() in jangle.html.
const KB_STYLES = [
  { value: "bright", label: "Brighter key", note: "A subtle lighter key for your tiles.",
    on: { background: "#4a4775", borderColor: "rgba(255,255,255,0.28)" } },
  { value: "pink", label: "Pink", note: "Keys for your tiles turn pink, like the tiles.",
    on: { background: "#f393a1", borderColor: "#d9677a", textShadow: "0 1px 2px rgba(0,0,0,.45)" } },
  { value: "off", label: "Off", note: "Every key looks the same.", on: null },
];
const PREVIEW = "QWERTYUIOP".split("");
const PREVIEW_HAND = new Set("ERUIO".split(""));

function KeyPreview({ on }) {
  return (
    <div className="flex gap-[3px] rounded-lg p-1.5" style={{ background: "#1c1a2e" }} aria-hidden>
      {PREVIEW.map((L) => (
        <span
          key={L}
          className="flex-1 min-w-0 h-7 rounded-[5px] border text-white text-[11px] font-semibold grid place-items-center"
          style={{ background: "#2a2840", borderColor: "rgba(255,255,255,0.1)", ...(on && PREVIEW_HAND.has(L) ? on : {}) }}
        >
          {L}
        </span>
      ))}
    </div>
  );
}

// TEEF on the board reads FEET, so the F carries a left arrow. Same look as
// .word-arrow in jangle.html.
function WordArrowPreview({ on }) {
  return (
    <div className="flex gap-[3px]" aria-hidden>
      {"TEEF".split("").map((L, i) => (
        <span
          key={i}
          className="relative w-8 h-8 rounded-[4px] grid place-items-center text-[13px] font-bold"
          style={{ background: "#9b5c70", border: "1.5px solid #f2a59c", color: "#fff" }}
        >
          {L}
          {on && i === 3 && (
            <svg viewBox="0 0 10 10" className="absolute right-0 bottom-0 w-[8px] h-[8px]"
              style={{ transform: "rotate(180deg)", fill: "none", stroke: "#fff", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M1.5 5h6M5 2l3 3-3 3" />
            </svg>
          )}
        </span>
      ))}
    </div>
  );
}

// Board styles (Jack, 2026-11-04): the usual board, or Fun (navy squares,
// green POW squares, chunky tiles and a castle round each base). The game
// draws it (JangleBoardStyle in jangle.html) on every board.
const BOARD_STYLES = [
  { value: "normal", label: "Normal", note: "Stream's own board." },
  { value: "basic", label: "Basic", note: "The board from the Live game." },
  { value: "fun", label: "Fantasy", note: "Navy squares, chunky tiles and a castle round each base." },
  { value: "mellow", label: "Mellow", note: "A calm navy checker with a light plate under each base." },
  { value: "wooden", label: "Wooden", note: "Letterheist's look and sounds: white squares in a wooden frame." },
];

function BoardPreview({ style }) {
  const fun = style === "fun";
  const mellow = style === "mellow";
  const wooden = style === "wooden";
  const basic = style === "basic";
  const cells = [];
  for (let i = 0; i < 21; i += 1) {
    const r = Math.floor(i / 7), c = i % 7;
    const pow = r === 2 && (c === 0 || c === 6);
    const tile = r === 0 && c >= 2 && c <= 4 ? "ABC"[c - 2] : null;
    const base = r === 2 && c >= 2 && c <= 4;
    let st;
    if (wooden) {
      st = tile ? { background: "#f393a1", color: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1.5px #d9677a" }
        : base ? { background: "#f393a1", borderRadius: 2, boxShadow: "inset 0 0 0 1px #d9677a" }
          : { background: "#fff", borderRadius: 2 };
    } else if (mellow) {
      st = tile ? { background: "#2f8ff0", color: "#fff", borderRadius: 4 }
        : pow ? { background: "#22335f", color: "#fff", borderRadius: 3, fontSize: 9 }
          : base ? { background: "#e7e4ec", borderRadius: 0 }
            : { background: i % 2 ? "#1b2a50" : "#22335f", borderRadius: 3 };
    } else if (fun) {
      st = tile ? { background: "#f4f6fb", color: "#16213f", borderRadius: 4, boxShadow: "inset 0 -2px 0 #c9cfdf" }
        : pow ? { background: "linear-gradient(#a6e05a,#77b52e)", borderRadius: 4 }
          : base ? { background: "#d8ccd3", borderRadius: 3, boxShadow: "inset 0 0 0 1.5px #7d6a76" }
            : { background: i % 2 ? "#1d2d5b" : "#26396e", borderRadius: 3 };
    } else if (!basic) {
      st = tile ? { background: "#f393a1", color: "#fff", borderRadius: 3, boxShadow: "inset 0 -2px 0 #d9677a" }
        : pow ? { background: "#8bc34a", borderRadius: 2 }
          : base ? { background: "#f393a1", borderRadius: 0 }
            : { background: "#1c2a52", borderRadius: 2 };
    } else {
      st = tile ? { background: "#f393a1", color: "#fff", borderRadius: 3 }
        : pow ? { background: "#3a3854", borderRadius: 2 }
          : base ? { background: "#9b5c70", borderRadius: 2 }
            : { background: "#2a2840", borderRadius: 2 };
    }
    cells.push(<span key={i} className="h-5 grid place-items-center text-[10px] font-bold" style={st}>{tile || (mellow && pow ? <span style={{ color: "#b08cff" }}>◆</span> : null)}</span>);
  }
  return (
    <div className="grid grid-cols-7 gap-[2px] rounded-lg p-1" aria-hidden
      style={{ background: wooden ? "#c3ccd8" : basic ? "#1c1a2e" : !mellow && !fun ? "#0a1124" : fun ? "#13204a" : mellow ? "#141f3d" : "#1c1a2e", boxShadow: wooden ? "0 0 0 4px #8a5d34, 0 0 0 5.5px #a8784a" : fun ? "0 0 0 1.5px #3f7bff" : mellow ? "0 0 0 1px #283a68" : "none", margin: wooden ? 5 : 0 }}>
      {cells}
    </div>
  );
}

export default function SettingsDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const current = { ...SETTINGS_DEFAULTS, ...readLocalSettings(), ...(user?.settings || {}) };
  const [saving, setSaving] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("jangle:sound") !== "off"; } catch { return true; }
  });
  const mine = resolveColours(current.colour, "default").me;

  // Stream board style is for this visit only: every time the app opens it
  // starts on Normal (Jack, 2026-11-04). Kept in sessionStorage, which the game
  // page (same tab) reads; never saved to the account.
  const [boardStyle, setBoardStyle] = useState(() => {
    try { return sessionStorage.getItem("jangle:board-style") || "normal"; } catch { return "normal"; }
  });
  const pickBoardStyle = (v) => {
    setBoardStyle(v);
    try { sessionStorage.setItem("jangle:board-style", v); } catch { /* private mode */ }
  };
  const change = async (key, value) => {
    if (current[key] === value) return;
    const next = { ...current, [key]: value };
    const prevUser = user;
    // Apply straight away (the game picks it up live), then save to the account.
    writeLocalSettings(next);
    setUser((u) => (u ? { ...u, settings: next } : u));
    setSaving(true);
    try {
      const { data } = await api.put("/me/settings", { [key]: value });
      setUser(data);
      if (key === "crown") refreshCrowns();
    } catch (e) {
      writeLocalSettings(current);
      setUser(prevUser);
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save that setting");
    } finally {
      setSaving(false);
    }
  };

  const pickSound = (on) => {
    setSoundOn(on);
    try { localStorage.setItem("jangle:sound", on ? "on" : "off"); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Settings</DialogTitle>
          <DialogDescription>Saved to your account, so they follow you to every device.</DialogDescription>
        </DialogHeader>

        {/* Game sounds on or off, first in Settings (Jack, 2026-11-04). Kept on
            this device; the game page checks jangle:sound before each sound. */}
        <section>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Sound</h3>
              <p className="text-xs text-muted-foreground mb-1">{soundOn ? "On. Tap the speaker to mute game sounds on this device." : "Muted on this device. Tap the speaker to turn sounds back on."}</p>
            </div>
            {/* Jack, 2026-11-04: a speaker icon instead of a switch; tap to mute. */}
            <button
              type="button"
              onClick={() => pickSound(!soundOn)}
              aria-pressed={!soundOn}
              aria-label={soundOn ? "Mute sound" : "Turn sound on"}
              title={soundOn ? "Tap to mute" : "Tap to turn sound on"}
              data-testid="setting-sound"
              data-on={soundOn ? "1" : "0"}
              className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 transition-colors active:scale-95 ${soundOn ? "border-brand bg-brand/15 text-brand" : "border-border bg-background text-muted-foreground"}`}
            >
              {soundOn ? <SpeakerHigh size={24} weight="fill" /> : <SpeakerSlash size={24} weight="fill" />}
            </button>
          </div>
        </section>

        {/* Light or dark (moved here from the button under the board, Jack
            2026-10-20). Same as that button: it lasts for this visit, and
            every visit starts in dark mode. */}
        <section>
          <h3 className="text-sm font-semibold">Light or dark</h3>
          <div role="radiogroup" aria-label="Light or dark" className="mt-2 grid grid-cols-2 gap-2">
            {[
              { value: "dark", label: "Dark", Icon: Moon },
              { value: "light", label: "Light", Icon: Sun },
            ].map(({ value, label, Icon }) => {
              const picked = theme === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={picked}
                  onClick={() => { if (!picked) toggleTheme(); }}
                  data-testid={`setting-theme-${value}`}
                  className={`flex items-center justify-center gap-2 rounded-xl border p-2.5 text-sm font-medium transition-colors ${
                    picked ? "border-brand ring-2 ring-brand/30" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <Icon size={16} weight="fill" /> {label}
                </button>
              );
            })}
          </div>
        </section>

        {/* My colour (Jack, 2026-11-04): one colour for your tiles, base and
            flag in every game. Opponents see it too; if theirs is the same,
            each sees the other in blue (pink if they're blue). */}
        <section>
          <h3 className="text-sm font-semibold">My colour</h3>
          <p className="text-xs text-muted-foreground mb-3">Your tiles, base and flag in every game.</p>
          <div role="radiogroup" aria-label="My colour" className="grid grid-cols-5 gap-1.5">
            {PLAYER_COLOURS.map((c) => {
              const picked = mine.value === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={picked}
                  disabled={saving}
                  onClick={() => change("colour", c.value)}
                  data-testid={`setting-colour-${c.value}`}
                  className={`rounded-xl border px-1 pt-2 pb-1.5 text-center text-xs transition-colors ${
                    picked ? "border-brand ring-2 ring-brand/30" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <span
                    className="mx-auto mb-1 grid h-7 w-7 place-items-center rounded-[7px]"
                    style={{ background: c.fill, border: `2px solid ${c.edge}`, color: c.ink || "#fff" }}
                  >
                    {picked && <Check size={13} weight="bold" />}
                  </span>
                  {c.label}
                  {c.value === "pink" && <span className="block text-[10px] text-muted-foreground">default</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex gap-1" aria-hidden>
            {"JANGLE".split("").map((L, i) => (
              <span
                key={i}
                className="grid h-7 w-7 place-items-center rounded-[4px] text-[13px] font-bold"
                style={{ background: mine.fill, border: `2px solid ${mine.edge}`, color: mine.ink || "#fff", textShadow: mine.ink ? "none" : "0 1px 2px rgba(0,0,0,.45)" }}
              >
                {L}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            If your opponent picked the same colour, you see them in blue (or pink if you&apos;re blue). They see you the same way.
          </p>
        </section>

        {/* Board style (Jack, 2026-11-04), Stream only, for every board; Stream's testers only. */}
        {user?.maze_test && (
        <section>
          <h3 className="text-sm font-semibold">Stream board style</h3>
          <p className="text-xs text-muted-foreground mb-3">How the board looks when you play in Stream, on every board. It starts on Normal each time you open the app. The normal game keeps its usual board.</p>
          <div role="radiogroup" aria-label="Board style" className="grid grid-cols-2 gap-2">
            {BOARD_STYLES.map((b) => {
              const picked = boardStyle === b.value;
              return (
                <button
                  key={b.value}
                  type="button"
                  role="radio"
                  aria-checked={picked}
                  disabled={saving}
                  onClick={() => pickBoardStyle(b.value)}
                  data-testid={`setting-board-${b.value}`}
                  className={`text-left rounded-xl border p-2 transition-colors ${
                    picked ? "border-brand ring-2 ring-brand/30" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <BoardPreview style={b.value} />
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="text-sm font-medium">{b.label}</div>
                    <span className={`w-5 h-5 rounded-full border grid place-items-center shrink-0 ${picked ? "bg-brand border-brand text-brand-foreground" : "border-muted-foreground/40"}`}>
                      {picked && <Check size={12} weight="bold" />}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground leading-tight">{b.note}</div>
                </button>
              );
            })}
          </div>
        </section>
        )}

        <section>
          <h3 className="text-sm font-semibold">Phone keyboard highlights</h3>
          <p className="text-xs text-muted-foreground mb-3">How the in-game keyboard shows the letters in your hand.</p>
          <div role="radiogroup" aria-label="Phone keyboard highlights" className="space-y-2">
            {KB_STYLES.map((s) => {
              const picked = current.kb_highlight === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  aria-checked={picked}
                  disabled={saving}
                  onClick={() => change("kb_highlight", s.value)}
                  data-testid={`setting-kb-${s.value}`}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${
                    picked ? "border-brand ring-2 ring-brand/30" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-medium">{s.label}{s.value === SETTINGS_DEFAULTS.kb_highlight ? " (default)" : ""}</div>
                      <div className="text-xs text-muted-foreground">{s.note}</div>
                    </div>
                    <span className={`w-5 h-5 rounded-full border grid place-items-center shrink-0 ${picked ? "bg-brand border-brand text-brand-foreground" : "border-muted-foreground/40"}`}>
                      {picked && <Check size={12} weight="bold" />}
                    </span>
                  </div>
                  <KeyPreview on={s.on} />
                </button>
              );
            })}
          </div>
        </section>

        <section className="pt-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Word direction arrows</h3>
              <p className="text-xs text-muted-foreground mb-3">
                A small arrow on each word&apos;s first letter shows which way it reads.
              </p>
            </div>
            <Switch
              checked={current.word_arrows === "on"}
              disabled={saving}
              onCheckedChange={(v) => change("word_arrows", v ? "on" : "off")}
              aria-label="Word direction arrows"
              data-testid="setting-word-arrows"
            />
          </div>
          <WordArrowPreview on={current.word_arrows === "on"} />
        </section>

        {/* Gold crown for a leaderboard #1 (Jack, 2026-10-29). Only matters
            while you lead, but anyone can set it ahead of time. */}
        <section className="pt-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Show my leader&apos;s crown</h3>
              <p className="text-xs text-muted-foreground">
                When you&apos;re number 1 on a leaderboard, a gold crown shows by your name and raider.
              </p>
            </div>
            <Switch
              checked={current.crown !== "off"}
              disabled={saving}
              onCheckedChange={(v) => change("crown", v ? "on" : "off")}
              aria-label="Show my leader's crown"
              data-testid="setting-crown"
            />
          </div>
        </section>
      </DialogContent>
    </Dialog>
  );
}
